import React, { useState, useEffect } from "react";
import {
  Tooltip,
  Button,
  Card,
  CardBody,
  Input,
  Tab,
  Tabs,
  SortDescriptor,
} from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-custom.css";

import { salesAPI } from "@/services/api";
import { EyeIcon, SearchIcon } from "@/components/icons";
import DefaultLayout from "@/layouts/default";
import { useDirectionLanguage } from "@/components/DirectionLanguageContext";
import DataTable, { Column } from "@/components/DataTable";
import { formatDate } from "@/utils/dateUtils";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface SaleItem {
  book_id: string;
  book_name: string;
  quantity: number;
  price_per_book: number;
  subtotal: number;
}

interface Sale {
  id: string;
  sale_date: string;
  buyer_name: string;
  buyer_phone: string;
  total_price: number;
  client_id: string;
  items: SaleItem[];
  invoice_number?: string;
  created_at?: string;
  updated_at?: string;
}

// For sales metrics over different time periods
interface SalesMetrics {
  count: number;
  revenue: number;
  quantity: number;
}

const SalesPage: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [filteredSales, setFilteredSales] = useState<Sale[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "sale_date",
    direction: "descending",
  });

  // Metrics for different time periods
  const [totalMetrics, setTotalMetrics] = useState<SalesMetrics>({
    count: 0,
    revenue: 0,
    quantity: 0,
  });
  const [monthlyMetrics, setMonthlyMetrics] = useState<SalesMetrics>({
    count: 0,
    revenue: 0,
    quantity: 0,
  });
  const [dailyMetrics, setDailyMetrics] = useState<SalesMetrics>({
    count: 0,
    revenue: 0,
    quantity: 0,
  });
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("total");

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRtl } = useDirectionLanguage();

  // Calculate metrics for different time periods
  const calculateTimeMetrics = (salesList: Sale[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Initialize metrics
    const daily = { count: 0, revenue: 0, quantity: 0 };
    const monthly = { count: 0, revenue: 0, quantity: 0 };
    const total = {
      count: salesList.length,
      revenue: 0,
      quantity: salesList.reduce((sum, sale) => {
        return (
          sum + sale.items.reduce((itemSum, item) => itemSum + item.quantity, 0)
        );
      }, 0),
    };

    // Calculate total revenue
    total.revenue = salesList.reduce(
      (sum, sale) => sum + Number(sale.total_price),
      0
    );

    salesList.forEach((sale) => {
      // Try to parse the sale date
      const saleDate = new Date(sale.sale_date);
      const itemQuantity = sale.items.reduce(
        (sum, item) => sum + item.quantity,
        0
      );

      if (!isNaN(saleDate.getTime())) {
        // Add to monthly metrics if sale is in current month
        if (saleDate >= firstDayOfMonth) {
          monthly.count++;
          monthly.revenue += Number(sale.total_price);
          monthly.quantity += itemQuantity;

          // Add to daily metrics if sale is today
          if (saleDate >= today) {
            daily.count++;
            daily.revenue += Number(sale.total_price);
            daily.quantity += itemQuantity;
          }
        }
      }
    });

    setDailyMetrics(daily);
    setMonthlyMetrics(monthly);
    setTotalMetrics(total);
  };

  // Filter sales by all criteria: search query and date range
  const filterSales = (allSales: Sale[]) => {
    let filtered = [...allSales];

    // Apply date range filter if both dates are set
    if (startDate && endDate) {
      const startTimestamp = startDate.setHours(0, 0, 0, 0);
      const endTimestamp = endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter((sale) => {
        const saleDate = new Date(sale.sale_date);
        const saleTimestamp = saleDate.getTime();

        return saleTimestamp >= startTimestamp && saleTimestamp <= endTimestamp;
      });
    }

    // Apply search query filter
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter(
        (sale) =>
          sale.buyer_name.toLowerCase().includes(query) ||
          sale.buyer_phone?.toLowerCase().includes(query) ||
          sale.total_price.toString().includes(query) ||
          formatDate(sale.sale_date).toLowerCase().includes(query) ||
          // Search in items as well
          sale.items.some(
            (item) =>
              item.book_name.toLowerCase().includes(query) ||
              item.quantity.toString().includes(query) ||
              item.price_per_book.toString().includes(query) ||
              item.subtotal.toString().includes(query)
          )
      );
    }

    // Ensure results are always sorted by date (newest first)
    filtered.sort(
      (a, b) =>
        new Date(b.sale_date).getTime() - new Date(a.sale_date).getTime()
    );

    setFilteredSales(filtered);
    calculateTimeMetrics(filtered);

    // Reset sort descriptor to ensure consistent serial numbering
    setSortDescriptor({
      column: "sale_date",
      direction: "descending",
    });
  };

  // Handle sort change for DataTable
  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();

    setSearchQuery(query);
    filterSales(sales);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setStartDate(null);
    setEndDate(null);
    setFilteredSales(sales);
    calculateTimeMetrics(sales);
  };

  // Helper functions for preset date ranges
  const generateDailyReport = () => {
    const today = new Date();
    setStartDate(today);
    setEndDate(today);
    // filterSales will be called by useEffect when dates change
  };

  const generateWeeklyReport = () => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    setStartDate(weekAgo);
    setEndDate(today);
    // filterSales will be called by useEffect when dates change
  };

  const generateMonthlyReport = () => {
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(today.getDate() - 30);
    setStartDate(monthAgo);
    setEndDate(today);
    // filterSales will be called by useEffect when dates change
  };

  // Format currency
  const formatCurrency = (
    value: number | string | null | undefined
  ): string => {
    if (value === null || value === undefined || value === "") return "Rs0.00";

    const numericValue = typeof value === "string" ? parseFloat(value) : value;

    if (typeof numericValue !== "number" || isNaN(numericValue)) {
      return "Rs0.00";
    }

    return `Rs${numericValue.toFixed(2)}`;
  };

  // Download PDF report function with RTL and Urdu support
  const downloadPDF = async () => {
    if (filteredSales.length === 0) {
      alert(t("NoDataToDownload", "No sales data available to download."));
      return;
    }

    setIsDownloading(true);
    setError(null);

    let pdfContainer: HTMLDivElement | null = null;

    try {
      // Preload and wait for the Urdu font to be ready
      const fontUrl = '/fonts/Mehr_Nastaliq_Web v.2.0.woff';
      const fontName = 'Mehr Nastaliq';
      
      // Check if font is already loaded, if not, load it
      try {
        if (!document.fonts.check(`16px "${fontName}"`)) {
          const fontFace = new FontFace(fontName, `url(${fontUrl})`);
          await fontFace.load();
          document.fonts.add(fontFace);
          // Wait a bit more to ensure font is fully ready for rendering
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (fontError) {
        console.warn("Font loading error, continuing with fallback:", fontError);
      }

      // Create a temporary container for the PDF content with RTL support
      pdfContainer = document.createElement("div");
      pdfContainer.style.position = "absolute";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.top = "0";
      pdfContainer.style.width = "210mm"; // A4 width
      pdfContainer.style.padding = "20mm";
      pdfContainer.style.backgroundColor = "white";
      pdfContainer.style.direction = "rtl";
      pdfContainer.style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
      pdfContainer.style.fontSize = "12px";
      pdfContainer.style.color = "#000";
      pdfContainer.dir = "rtl";

      // Add font-face style to the container with absolute URL
      const fontStyle = document.createElement("style");
      const baseUrl = window.location.origin;
      fontStyle.textContent = `
        @font-face {
          font-family: '${fontName}';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        @font-face {
          font-family: 'Mehr Nastaliq Web';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        * {
          font-family: "${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif !important;
        }
      `;
      pdfContainer.appendChild(fontStyle);

      // Helper function to create table HTML with header
      const createTableHTML = (rows: Sale[], startIndex: number) => {
        const headerHTML = `
          <thead>
            <tr style="background-color: #4285c4; color: white;">
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("S.No", "نمبر")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("invoice_number")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("BuyerName", "خریدار کا نام")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("SaleDate", "فروخت کی تاریخ")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("Items", "آئٹمز")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalAmount", "کل رقم")}</th>
            </tr>
          </thead>
        `;

        const rowsHTML = rows
          .map(
            (sale, idx) => {
              const itemCount = sale.items?.length || 0;
              return `
              <tr style="background-color: ${idx % 2 === 0 ? "#f9f9f9" : "white"};">
                <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${startIndex + idx + 1}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif; font-weight: bold; color: #4285c4;">${sale.invoice_number || "-"}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${sale.buyer_name || "-"}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatDate(sale.sale_date)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${itemCount}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(sale.total_price)}</td>
              </tr>
            `;
            }
          )
          .join("");

        return `
          <table style="width: 100%; border-collapse: collapse; direction: rtl;">
            ${headerHTML}
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>
        `;
      };

      // Create header HTML (title, date range, summary)
      const now = new Date();
      const formattedStartDate = startDate 
        ? new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().split("T")[0]
        : "";
      const formattedEndDate = endDate
        ? new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().split("T")[0]
        : "";
      
      const dateRangeText = startDate && endDate
        ? `${formattedStartDate} ${t("To", "سے")} ${formattedEndDate} ${t("To", "تک")}`
        : t("AllSales", "تمام فروخت");

      // Calculate summary for filtered sales
      const totalRevenue = filteredSales.reduce((sum, sale) => sum + Number(sale.total_price), 0);
      const totalQuantity = filteredSales.reduce((sum, sale) => 
        sum + sale.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0);

      const headerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 24px; margin-bottom: 15px; color: #333; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            ${t("SalesReport", "فروخت کی رپورٹ")}
          </h1>
          <p style="font-size: 14px; margin: 5px 0; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            <strong>${t("ReportPeriod", "رپورٹ کا دورانیہ")}:</strong> ${dateRangeText}
          </p>
          <p style="font-size: 12px; margin: 5px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            ${t("GeneratedOn", "وقت اجراء رپورٹ")}: ${now.toLocaleDateString("ur-PK")} - ${now.toLocaleTimeString("ur-PK")}
          </p>
          ${searchQuery ? `<p style="font-size: 12px; margin: 5px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("Filtering by", "فلٹر")}: ${searchQuery}</p>` : ""}
        </div>

        <div style="margin-bottom: 30px; display: flex; justify-content: space-around; flex-wrap: wrap; gap: 15px;">
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalSales", "کل فروخت")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${filteredSales.length}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalRevenue", "کل آمدنی")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(totalRevenue)}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("BooksSold", "کل کتابیں فروخت")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${totalQuantity}</p>
          </div>
        </div>
      `;

      // Estimate rows per page (conservative estimate to ensure rows don't split)
      const rowsPerPageFirst = 20; // Conservative for first page
      const rowsPerPageSubsequent = 24; // Conservative for subsequent pages

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm

      // Split data into chunks and create pages
      const totalRows = filteredSales.length;
      let currentIndex = 0;
      let pageNumber = 0;

      while (currentIndex < totalRows) {
        const rowsPerPage = pageNumber === 0 ? rowsPerPageFirst : rowsPerPageSubsequent;
        const chunk = filteredSales.slice(currentIndex, currentIndex + rowsPerPage);
        
        // Create page content - header only on first page, table header on every page
        pdfContainer.innerHTML = pageNumber === 0
          ? headerHTML + createTableHTML(chunk, currentIndex)
          : `<div style="margin-bottom: 10px;"></div>` + createTableHTML(chunk, currentIndex); // Small margin for subsequent pages

        document.body.appendChild(pdfContainer);
        
        // Reduced wait time for faster rendering
        // Wait longer for font to render properly
        await new Promise((resolve) => setTimeout(resolve, 500));

        try {
          const canvas = await html2canvas(pdfContainer, {
            scale: 1.5, // Reduced from 2 to 1.5 for faster rendering while maintaining quality
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            windowWidth: pdfContainer.scrollWidth,
            windowHeight: pdfContainer.scrollHeight,
            removeContainer: false, // Keep container for cleanup
            onclone: (clonedDoc) => {
              // Ensure font is applied in the cloned document
              const clonedContainer = clonedDoc.querySelector('div[dir="rtl"]');
              if (clonedContainer) {
                (clonedContainer as HTMLElement).style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
              }
            },
          });

          document.body.removeChild(pdfContainer);

          // Add to PDF
          if (pageNumber > 0) {
            pdf.addPage();
          }

          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          // Use JPEG with quality 0.92 for smaller file size and faster processing
          const imgData = canvas.toDataURL("image/jpeg", 0.92);
          pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);

          currentIndex += rowsPerPage;
          pageNumber++;
        } catch (canvasError) {
          // Clean up container if canvas fails
          if (pdfContainer.parentNode) {
            document.body.removeChild(pdfContainer);
          }
          throw canvasError;
        }
      }

      // Generate filename
      const filename = startDate && endDate
        ? `Sales_Report_${formattedStartDate}_to_${formattedEndDate}.pdf`
        : `Sales_Report_${now.toISOString().split("T")[0]}.pdf`;

      // Save the PDF - ensure it's saved properly
      try {
        pdf.save(filename);
        console.log(`PDF report downloaded as: ${filename}`);
        // Show success message
        alert(t("PDFDownloadedSuccessfully", "PDF downloaded successfully!"));
      } catch (saveError) {
        console.error("Error saving PDF:", saveError);
        throw new Error("Failed to save PDF file");
      }
    } catch (error: any) {
      console.error("PDF generation error:", error);
      
      // Clean up container if it still exists
      if (pdfContainer && pdfContainer.parentNode) {
        document.body.removeChild(pdfContainer);
      }
      
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to generate PDF report";
      setError(
        t("ErrorDownloadingReport", "Error downloading report: ") + errorMessage
      );
      alert(t("ErrorDownloadingReport", "Error downloading report: ") + errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const fetchSales = async () => {
      setLoading(true);
      setError(null);
      try {
        const clientId = localStorage.getItem("clientId");

        if (!clientId) throw new Error("Client ID is not set");

        const salesList = await salesAPI.getAll(clientId);

        // Ensure we have the correct data structure for sales
        const processedSales = Array.isArray(salesList)
          ? salesList.map((sale: any) => ({
              id: sale.id,
              sale_date:
                sale.sale_date ||
                sale.saleDate ||
                new Date().toISOString().split("T")[0],
              buyer_name: sale.buyer_name || sale.buyerName || "",
              buyer_phone: sale.buyer_phone || sale.buyerPhone || "",
              total_price: Number(sale.total_price || sale.totalPrice) || 0,
              client_id: sale.client_id || sale.clientId || clientId,
              invoice_number: sale.invoice_number || sale.invoiceNumber || "",
              items: Array.isArray(sale.items)
                ? sale.items.map((item: any) => ({
                    book_id: item.book_id || item.bookId,
                    book_name: item.book_name || item.bookName,
                    quantity: Number(item.quantity) || 1,
                    price_per_book:
                      Number(item.price_per_book || item.pricePerBook) || 0,
                    subtotal:
                      Number(item.subtotal) ||
                      Number(item.price_per_book || item.pricePerBook) *
                        (Number(item.quantity) || 1),
                  }))
                : [],
              created_at: sale.created_at,
              updated_at: sale.updated_at,
            }))
          : [];

        // Sort by date (newest first)
        processedSales.sort(
          (a, b) =>
            new Date(b.sale_date).getTime() - new Date(a.sale_date).getTime()
        );

        setSales(processedSales);
        setFilteredSales(processedSales);

        // Calculate metrics for different time periods
        calculateTimeMetrics(processedSales);
      } catch (error: any) {
        console.error("Error fetching sales:", error);
        setError(t("Failed to fetch sales."));
      } finally {
        setLoading(false);
      }
    };

    fetchSales();
  }, [t]);

  // Update filtered sales when filters change
  useEffect(() => {
    filterSales(sales);
  }, [searchQuery, startDate, endDate]);

  const handleView = (id: string) => {
    navigate(`/sale/View/${id}`);
  };

  // const handleEdit = (id: string) => {
  //   navigate(`/saleEdit/${id}`);
  // };

  // const handleDelete = async (id: string) => {
  //   const saleToDelete = sales.find((sale) => sale.id === id);

  //   if (saleToDelete) {
  //     const confirmDelete = window.confirm(t("DeleteSaleConfirm"));

  //     if (confirmDelete) {
  //       try {
  //         await salesAPI.delete(id);

  //         // Remove from sales list
  //         const updatedSales = sales.filter((sale) => sale.id !== id);

  //         setSales(updatedSales);
  //         setFilteredSales(updatedSales);

  //         // Recalculate metrics
  //         calculateTimeMetrics(updatedSales);

  //         alert(t("Sale deleted successfully!"));
  //       } catch (error) {
  //         console.error("Error deleting sale:", error);
  //         alert(t("Failed to delete sale."));
  //       }
  //     }
  //   }
  // };

  // Generate PDF invoice for a sale
  const generateInvoicePDF = async (sale: Sale) => {
    try {
      // Open the Laravel backend URL in a new tab
      const clientId = localStorage.getItem("clientId") || undefined;
      const backendUrl = await salesAPI.generateInvoice(sale.id, clientId);
      window.open(backendUrl, "_blank");
    } catch (error) {
      console.error("Error generating invoice:", error);
      alert(t("Failed to generate invoice"));
    }
  };

  // Define columns for DataTable
  const columns: Column<Sale>[] = [
    {
      key: "invoice_number",
      label: t("invoice_number"),
      sortable: true
    },
    {
      key: "buyer_name",
      label: t("BuyerName"),
      sortable: true,
    },
    {
      key: "sale_date",
      label: t("SaleDate"),
      sortable: true,
      renderCell: (sale) => formatDate(sale.sale_date),
    },
    {
      key: "itemCount",
      label: t("Items"),
      renderCell: (sale) => <span>{sale.items?.length || 0}</span>,
    },
    {
      key: "total_price",
      label: t("TotalAmount"),
      sortable: true,
      renderCell: (sale) => (
        <span className="font-medium">
          {Number(sale.total_price).toFixed(2)}
        </span>
      ),
    },
    {
      key: "actions",
      label: t("Actions"),
      renderCell: (sale) => {
        return (
          <div className="relative flex justify-center items-center gap-2">
            <Tooltip content={t("ViewSale")}>
              <button
                aria-label={t("ViewSale")}
                className="text-lg text-default-400 cursor-pointer active:opacity-50"
                onClick={() => handleView(sale.id)}
              >
                <EyeIcon />
              </button>
            </Tooltip>
            {/* <Tooltip content={t("EditSale")}>
            <button
              aria-label={t("EditSale")}
              className="text-lg text-default-400 cursor-pointer active:opacity-50"
              onClick={() => handleEdit(sale.id)}
            >
              <EditIcon />
            </button>
          </Tooltip> */}
            <Tooltip content={t("GenerateInvoice")}>
              <button
                aria-label={t("GenerateInvoice")}
                className="text-lg text-success cursor-pointer active:opacity-50"
                onClick={() => generateInvoicePDF(sale)}
              >
                🧾
              </button>
            </Tooltip>
            {/* <Tooltip content={t("DeleteSale")}>
              <button
                aria-label={t("DeleteSale")}
                className="text-lg text-danger cursor-pointer active:opacity-50"
                onClick={() => handleDelete(sale.id)}
              >
                <DeleteIcon />
              </button>
            </Tooltip> */}
          </div>
        );
      },
    },
  ];

  // Get metrics based on selected timeframe
  const getSelectedMetrics = () => {
    switch (selectedTimeframe) {
      case "daily":
        return dailyMetrics;
      case "monthly":
        return monthlyMetrics;
      case "total":
      default:
        return totalMetrics;
    }
  };

  const metrics = getSelectedMetrics();

  return (
    <DefaultLayout>
      <h1 className="text-3xl font-bold text-center mb-8">
        {t("SalesReport")}
      </h1>

      {/* Sales Summary Cards with Time Period Tabs */}
      <div className="px-8 mb-6">
        <Tabs
          aria-label="Time period options"
          className="mb-4"
          selectedKey={selectedTimeframe}
          onSelectionChange={(key) => setSelectedTimeframe(key as string)}
        >
          <Tab key="daily" title={t("Today")} />
          <Tab key="monthly" title={t("ThisMonth")} />
          <Tab key="total" title={t("AllTime")} />
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("TotalSales")}</p>
              <h3 className="text-2xl font-bold">{metrics.count}</h3>
              <p className="text-xs text-default-400">{t("Transactions")}</p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("TotalRevenue")}</p>
              <h3 className="text-2xl font-bold">
                {metrics.revenue.toFixed(2)}
              </h3>
              <p className="text-xs text-default-400">{t("SalesValue")}</p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("BooksSold")}</p>
              <h3 className="text-2xl font-bold">{metrics.quantity}</h3>
              <p className="text-xs text-default-400">{t("Units")}</p>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Quick Report Buttons */}
      <div className="flex justify-center mb-4 px-8">
        <div className="flex flex-wrap gap-2 justify-center">
          <Button
            color="success"
            variant="flat"
            onClick={generateDailyReport}
            size="md"
            className="min-w-[120px]"
          >
            {t("DailyReport", "Daily")}
          </Button>
          <Button
            color="warning"
            variant="flat"
            onClick={generateWeeklyReport}
            size="md"
            className="min-w-[120px]"
          >
            {t("WeeklyReport", "Weekly")}
          </Button>
          <Button
            color="secondary"
            variant="flat"
            onClick={generateMonthlyReport}
            size="md"
            className="min-w-[120px]"
          >
            {t("MonthlyReport", "Monthly")}
          </Button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-8 mb-4">
        <div className="flex w-full md:w-auto justify-between md:justify-start gap-2">
          <Button
            className="min-w-[140px]"
            color="primary"
            size="md"
            onClick={() => navigate("/addSale")}
          >
            {t("AddSale")}
          </Button>
          <Button
            className="min-w-[140px]"
            color="success"
            variant="bordered"
            size="md"
            onClick={downloadPDF}
            isLoading={isDownloading}
          >
            {t("Download PDF", "ڈاؤن لوڈ PDF")}
          </Button>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-4 w-full">
          <Input
            isClearable
            className="w-full md:max-w-[320px]"
            placeholder={t("SearchSales")}
            size="md"
            startContent={<SearchIcon className="text-default-400" />}
            value={searchQuery}
            onChange={handleSearchChange}
            onClear={() => {
              setSearchQuery("");
              filterSales(sales);
            }}
          />

          <div className="flex flex-row items-center gap-2 w-full md:justify-end">
            <div className="flex-none">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => {
                  setStartDate(date);
                  filterSales(sales);
                }}
                placeholderText={t("StartDate")}
                maxDate={endDate || new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex-none">
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => {
                  setEndDate(date);
                  filterSales(sales);
                }}
                placeholderText={t("EndDate")}
                minDate={startDate ? startDate : undefined}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="p-2 border border-gray-300 rounded-md"
              />
            </div>
            <Button
              className="min-w-[110px] flex-none"
              color="default"
              size="md"
              variant="flat"
              onClick={handleResetFilters}
            >
              {t("ResetFilters")}
            </Button>
          </div>
        </div>
      </div>

      {/* Active Filter Indicator */}
      {(searchQuery || startDate || endDate) && (
        <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700 flex items-center mx-8">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          <span>
            {t("FilteringBy")}:
            {searchQuery && (
              <span className="ml-1 font-medium">{t("Search")}</span>
            )}
            {startDate && (
              <span className="ml-1 font-medium">{t("StartDate")}</span>
            )}
            {endDate && (
              <span className="ml-1 font-medium">{t("EndDate")}</span>
            )}
          </span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-danger-100 text-danger rounded-md mx-8">
          {error}
        </div>
      )}

      <section
        className="row container px-8 flex flex-col items-center justify-center gap-4 py-2 overflow-x-hidden"
        dir={isRtl ? "rtl" : "ltr"}
        style={{ maxWidth: "100%", overflowX: "hidden" }}
      >
        <div className="w-full overflow-x-auto" style={{ maxWidth: "100%" }}>
          <DataTable
            columns={columns}
            data={filteredSales}
            isLoading={loading}
            emptyContent={
              searchQuery || startDate || endDate
                ? t("NoMatchingSalesFound")
                : t("NoSalesRecordsFound")
            }
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
            className="w-full"
            addSerialNumbers={true}
          />
        </div>
      </section>
    </DefaultLayout>
  );
};

export default SalesPage;
